import PuzzlePreview from '@/components/game/PuzzlePreview';
import SettingsMenuWrapper from '@/components/layout/SettingsMenuWrapper';
import {
  CharacterCustomizationModal,
  DailyRewardModal,
  LevelSelectModal,
  NoLivesModal,
  ProfileModal,
  ShopModal,
} from '@/components/modals';
import { GameButton, HighScores, PixelIcon, PlayerStatusBar } from '@/components/ui';
import { useGame } from '@/contexts/GameContext';
import { useShop } from '@/contexts/ShopContext';
import { puzzles } from '@/game/puzzles';
import { useUserProfile } from '@/hooks';
import { useKeyboardControls } from '@/hooks/useKeyboardControls';
import { useModalRouter } from '@/hooks/useModalRouter';
import { StorageService } from '@/services/storage';
import { getSavedInfiniteRunState, clearInfiniteRunState } from '@/hooks/useGameEngine';
import type { HomeScreenProps } from '@/types/game';
import { getPuzzleGrid } from '@/types/puzzle';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import HomeCharacter from './HomeCharacter';
import { FONT, FONT_SIZE, SPACING, RADIUS, COLOR, LAYOUT, glassPillStyle, resourceChipStyle } from '@/config/designTokens';

type HomeMode = 'story' | 'explore';

function HomeScreen({ onStartGame }: HomeScreenProps) {
  const { shopState, getFormattedTimeUntilNextLife, shopManager } = useShop();
  const maxLives = shopManager?.getMaxLives?.() ?? 5;
  const [nextLevelNumber, setNextLevelNumber] = useState(1);
  const [hasCompletedLevels, setHasCompletedLevels] = useState(false);
  const [showNoLivesModal, setShowNoLivesModal] = useState(false);
  const [showDailyRewardModal, setShowDailyRewardModal] = useState(false);
  const [nextLifeTimer, setNextLifeTimer] = useState<string>('');
  const [canClaimDailyReward, setCanClaimDailyReward] = useState(false);
  const [dailyStreak, setDailyStreak] = useState(0);
  const [statsExpanded, setStatsExpanded] = useState(false);
  const { gameState, updateGameState: updateContextGameState } = useGame();
  const navigate = useNavigate();
  const { activeModal, openModal, closeModal, isModalOpen } = useModalRouter();

  const [highScoresExpanded, setHighScoresExpanded] = useState(false);

  // Mode switcher -- persisted to localStorage, defaults to 'explore'
  const [activeMode, setActiveMode] = useState<HomeMode>(() => {
    const saved = localStorage.getItem('homeMode');
    return (saved === 'story' || saved === 'explore') ? saved : 'explore';
  });

  const handleModeChange = (mode: HomeMode) => {
    setActiveMode(mode);
    localStorage.setItem('homeMode', mode);
  };

  // Calculate next level based on completed levels from GameContext
  const calculateNextLevel = () => {
    const completedLevels = gameState.completedLevels || [];
    const validCompletedLevels = [...new Set(completedLevels)].filter(
      levelIndex => levelIndex >= 0 && levelIndex < puzzles.length
    );
    const maxCompletedLevelIndex =
      validCompletedLevels.length > 0 ? Math.max(...validCompletedLevels) : -1;
    const nextLevelIndex = maxCompletedLevelIndex + 1;
    if (nextLevelIndex >= puzzles.length) {
      return puzzles.length;
    }
    return nextLevelIndex + 1;
  };

  // Check if all levels are completed
  const allLevelsCompleted = () => {
    const completedLevels = gameState.completedLevels || [];
    const validCompletedLevels = [...new Set(completedLevels)].filter(
      levelIndex => levelIndex >= 0 && levelIndex < puzzles.length
    );
    return validCompletedLevels.length === puzzles.length;
  };
  const { profile } = useUserProfile();

  // Update next level when completed levels change
  useEffect(() => {
    const nextLevel = calculateNextLevel();
    const hasCompleted = gameState.completedLevels.length > 0;
    const allCompleted = allLevelsCompleted();

    setNextLevelNumber(nextLevel);
    setHasCompletedLevels(hasCompleted);
    StorageService.setGameCompletionMode(allCompleted);
  }, [gameState.completedLevels]);

  // Ensure GameContext is synced with latest localStorage data on mount
  useEffect(() => {
    const completedLevelsFromStorage = StorageService.getCompletedLevels();
    const levelStatsFromStorage = StorageService.getLevelStats();

    const validCompletedLevels = [...new Set(completedLevelsFromStorage)]
      .filter(levelIndex => levelIndex >= 0 && levelIndex < puzzles.length)
      .sort((a, b) => a - b);

    if (completedLevelsFromStorage.length !== validCompletedLevels.length) {
      StorageService.saveCompletedLevels(validCompletedLevels);
    }

    if (JSON.stringify(gameState.completedLevels) !== JSON.stringify(validCompletedLevels)) {
      updateContextGameState({
        completedLevels: validCompletedLevels,
        levelStats: levelStatsFromStorage,
      });
    }
  }, []); // Only run once on mount

  useEffect(() => {
    console.log('[GAME] Take Me Home - Enhanced Edition Starting...');
    console.log(`[PERF] Game loaded in ${performance.now().toFixed(2)}ms`);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then(() => console.log('[PWA] Service Worker registered'))
        .catch(err => console.log('PWA not available:', err));
    }

    if (shopManager) {
      const status = shopManager.getDailyRewardStatus();
      setCanClaimDailyReward(status.canClaim);
      setDailyStreak(status.streak);

      if (status.canClaim) {
        setTimeout(() => setShowDailyRewardModal(true), 1000);
      }
    }
  }, [shopManager]);

  const handleContinueGame = () => {
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }
    const continueLevel = StorageService.getContinueLevel();
    onStartGame(continueLevel);
  };

  const handleStartGame = (level?: number) => {
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }
    if (level !== undefined) {
      StorageService.saveLastPlayedLevel(level);
    }
    onStartGame(level);
  };

  const handleStartExplore = () => {
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }
    // Resumes from saved run state (or starts fresh if none saved)
    navigate('/game?mode=infinite');
  };

  const handleNewExploreRun = () => {
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }
    clearInfiniteRunState();
    navigate('/game?mode=infinite&fresh=1');
  };

  // Update life refill timer and daily reward status
  useEffect(() => {
    if (shopState.lives < maxLives) {
      const updateTimer = () => {
        setNextLifeTimer(getFormattedTimeUntilNextLife());
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setNextLifeTimer('');
    }
  }, [shopState.lives, maxLives, getFormattedTimeUntilNextLife]);

  useEffect(() => {
    if (!shopManager) return;
    const checkDailyReward = () => {
      const status = shopManager.getDailyRewardStatus();
      setCanClaimDailyReward(status.canClaim);
      setDailyStreak(status.streak);
    };
    const interval = setInterval(checkDailyReward, 60000);
    return () => clearInterval(interval);
  }, [shopManager]);

  // Setup keyboard controls for home screen
  useKeyboardControls({
    onMove: () => {},
    onRestart: () => {},
    onHome: () => {},
    onSettings: () => openModal('settings'),
    onShop: () => openModal('shop'),
    onStats: () => {},
    onPowerup: () => {},
    onEscape: () => {
      if (activeModal) {
        closeModal();
      }
    },
    onEnter: () => {
      if (activeMode === 'explore') {
        handleStartExplore();
      } else {
        if (hasCompletedLevels) { handleContinueGame(); } else { handleStartGame(); }
      }
    },
    onLevelSelect: () => openModal('levels'),
    onProfile: () => openModal('profile'),
    onWorlds: () => navigate('/worlds'),
    onUpgrades: () => navigate('/upgrades'),
    disabled: false,
    isHomeScreen: true,
  });

  const completedCount = [...new Set(gameState.completedLevels || [])].filter(
    i => i >= 0 && i < puzzles.length,
  ).length;

  const infiniteBest = parseInt(localStorage.getItem('infiniteHighestWave') || '0');
  const savedRun = getSavedInfiniteRunState();
  const savedWave = savedRun?.wave ?? 0;

  // Total score for high scores badge
  const totalScore = Object.values(gameState.levelStats).reduce(
    (sum, s) => sum + (s.bestScore || 0), 0
  );

  // -- Mode Switcher --
  const modeSwitcherStyle: React.CSSProperties = {
    ...glassPillStyle(),
    padding: '3px',
    gap: '2px',
    width: '100%',
    maxWidth: LAYOUT.homeButtonMaxWidth,
    margin: '0 auto',
    justifyContent: 'stretch',
  };

  const modeTabStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.xs,
    padding: `${SPACING.sm} ${SPACING.md}`,
    borderRadius: RADIUS.pill,
    border: 'none',
    background: isActive
      ? `linear-gradient(135deg, ${COLOR.actionResume}22, ${COLOR.actionResume}11)`
      : 'transparent',
    color: isActive ? COLOR.textWhite : COLOR.textSecondary,
    fontSize: FONT_SIZE.caption,
    fontWeight: isActive ? 700 : 500,
    fontFamily: FONT.heading,
    cursor: 'pointer',
    position: 'relative',
    transition: 'color 0.2s ease',
  });

  // Unified button container width
  const buttonContainerStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: LAYOUT.homeButtonMaxWidth,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.md,
    alignItems: 'center',
  };

  return (
    <div id="homescreen-container" className="homescreen-container">
      {/* Branded 2D backdrop */}
      <div className="welcome-background" aria-hidden="true">
        <div className="welcome-background__mesh" />
        <div className="welcome-background__grid" />
        <div className="welcome-background__aurora" />
        <div className="welcome-background__pulse welcome-background__pulse--greenwood" />
        <div className="welcome-background__pulse welcome-background__pulse--stonehollow" />
        <div className="welcome-background__pulse welcome-background__pulse--frostline" />
        <div className="welcome-background__pulse welcome-background__pulse--emberdeep" />
        <div className="welcome-background__pulse welcome-background__pulse--arcana" />
        <div className="welcome-background__spark welcome-background__spark--1" />
        <div className="welcome-background__spark welcome-background__spark--2" />
        <div className="welcome-background__spark welcome-background__spark--3" />
        <div className="welcome-background__spark welcome-background__spark--4" />
        <div className="welcome-background__spark welcome-background__spark--5" />
        <div className="welcome-background__spark welcome-background__spark--6" />
        <div className="welcome-background__spark welcome-background__spark--7" />
        <div className="welcome-background__spark welcome-background__spark--8" />
      </div>

      <div id="title-screen" className="title-screen">
        {/* 1. Collapsible stats bar */}
        <div className="home-stats-section">
          <motion.button
            type="button"
            className="home-stats-summary"
            onClick={() => setStatsExpanded(prev => !prev)}
            whileTap={{ scale: 0.97 }}
            style={{
              ...glassPillStyle(),
              width: '100%',
              maxWidth: 420,
              justifyContent: 'center',
              margin: '0 auto',
            }}
          >
            <span style={resourceChipStyle()}>
              <PixelIcon name="life" size={14} color={COLOR.lifeLight} />
              {shopState.lives}
            </span>
            <span style={resourceChipStyle()}>
              <PixelIcon name="coin" size={14} color={COLOR.coinLight} />
              {shopState.coins}
            </span>
            <span style={resourceChipStyle()}>
              <PixelIcon name="gem" size={14} color={COLOR.gemLight} />
              {shopState.gems}
            </span>
            <motion.span
              animate={{ rotate: statsExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', marginLeft: SPACING.xs }}
            >
              <PixelIcon name="chevronDown" size={12} color="rgba(255,255,255,0.5)" />
            </motion.span>
          </motion.button>

          <AnimatePresence>
            {statsExpanded && (
              <motion.div
                key="stats-detail"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ overflow: 'hidden', width: '100%' }}
              >
                <PlayerStatusBar
                  className="home-status-bar"
                  mode="home"
                  lives={shopState.lives}
                  coins={shopState.coins}
                  gems={shopState.gems}
                  streak={dailyStreak}
                  canClaimStreak={canClaimDailyReward}
                  nextLifeTimer={shopState.lives < maxLives ? nextLifeTimer : null}
                  onStreakClick={() => setShowDailyRewardModal(true)}
                />
                <div className="flex items-center justify-center gap-2 mt-2 text-xs opacity-70">
                  <PixelIcon name="levels" size={12} color="#64B5F6" />
                  <span>{completedCount} / {puzzles.length} levels completed</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 2. Title */}
        <div className="title-screen__title text-center leading-[1]">
          <div id="custom-title-svg" className="custom-title-svg">
            Take Me
          </div>
          <span className="title-screen__word block">HOME</span>
        </div>

        {/* 3. Character window */}
        <div
          className="character-window cursor-pointer rounded-3xl border-[3px] border-white/5 hover:border-white/70 hover:scale-105 hover:-rotate-[3deg]"
          onClick={() => openModal('customize')}
          style={{
            transition: 'transform .3s ease, border-color .3s ease',
          }}
        >
          <div className="window-frame">
            <div className="window-pane">
              <div className="game-world-preview"></div>
            </div>
            <button className="customize-character-btn" title="Player Name">
              <span>{profile.username}</span>
            </button>
            <div className="window-character">
              <HomeCharacter />
            </div>
          </div>
        </div>

        {/* 4. Mode Switcher */}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <div style={modeSwitcherStyle}>
            {(['story', 'explore'] as const).map(mode => (
              <motion.button
                key={mode}
                type="button"
                onClick={() => handleModeChange(mode)}
                style={modeTabStyle(activeMode === mode)}
                whileTap={{ scale: 0.96 }}
              >
                <PixelIcon
                  name={mode === 'story' ? 'levels' : 'lightning'}
                  size={12}
                  color={activeMode === mode ? COLOR.actionResume : COLOR.textSecondary}
                />
                <span>{mode === 'story' ? 'Story Mode' : 'Explore Mode'}</span>
                {activeMode === mode && (
                  <motion.div
                    layoutId="mode-indicator"
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: '20%',
                      right: '20%',
                      height: 2,
                      borderRadius: 1,
                      background: COLOR.actionResume,
                    }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* 5. Mode Content */}
        <AnimatePresence mode="wait">
          {activeMode === 'explore' ? (
            <motion.div
              key="explore-content"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              {/* Explore Mode preview */}
              <div id="title-screen-levels" className="title-screen-levels">
                <div className="w-full flex justify-center items-center gap-2 mb-2">
                  <PixelIcon name="lightning" size={16} color={COLOR.gemLight} />
                  <p className="text-sm font-semibold" style={{ color: '#e0d4f7' }}>Explore Mode</p>
                </div>
                <p className="text-xs opacity-60 mb-2 text-center">
                  {savedWave > 0
                    ? `Wave ${savedWave}${infiniteBest > 0 ? ` | Best: ${infiniteBest}` : ''}`
                    : infiniteBest > 0
                    ? `Highest wave: ${infiniteBest}`
                    : 'Endless procedural levels'}
                </p>
              </div>

              {/* Explore Mode buttons */}
              <div
                id="title-screen-buttons-container"
                className="title-screen-buttons-container w-full flex flex-col items-center"
                style={buttonContainerStyle}
              >
                <GameButton
                  onClick={handleStartExplore}
                  variant={shopState.lives <= 0 ? 'danger' : 'primary'}
                  icon={shopState.lives <= 0 ? 'heart' : savedWave > 0 ? 'continue' : 'play'}
                  text={
                    shopState.lives <= 0
                      ? 'No Lives - Visit Shop'
                      : savedWave > 0
                      ? `Continue Wave ${savedWave}`
                      : 'Start Exploring'
                  }
                  shortcut="ENTER"
                  className="home-menu-btn"
                  layoutId="main-play-button"
                />

                {savedWave > 0 && shopState.lives > 0 && (
                  <GameButton
                    onClick={handleNewExploreRun}
                    variant="secondary"
                    icon="retry"
                    text="New Run"
                    className="home-menu-btn"
                  />
                )}

                <GameButton
                  onClick={() => openModal('levels')}
                  variant="secondary"
                  icon="levels"
                  text="World Map"
                  className="home-menu-btn"
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="story-content"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              {/* Story Mode preview */}
              <div id="title-screen-levels" className="title-screen-levels">
                {allLevelsCompleted() ? (
                  <>
                    <div className="w-full flex justify-center items-center gap-2 mb-3">
                      <PixelIcon name="star" size={18} color="#FFD700" />
                      <p className="text-sm font-semibold">Master Complete</p>
                      <PixelIcon name="star" size={18} color="#FFD700" />
                    </div>

                    {StorageService.getLastPlayedLevel() !== null &&
                      puzzles[StorageService.getLastPlayedLevel()!] && (
                        <div className="mb-3">
                          <p className="text-xs opacity-60 mb-1 text-center">Last played: {
                            StorageService.getLastPlayedLevel() === 0
                              ? 'Tutorial'
                              : `Level ${StorageService.getLastPlayedLevel()! + 1}`
                          }</p>
                          <PuzzlePreview
                            levelData={getPuzzleGrid(puzzles[StorageService.getLastPlayedLevel()!])}
                            layoutId="level-preview"
                            onClick={() => handleStartGame(StorageService.getLastPlayedLevel()!)}
                          />
                        </div>
                      )}

                    <div className="flex gap-2 items-center justify-center">
                      <div
                        className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-lg cursor-pointer hover:bg-white/20 transition-colors text-xs"
                        onClick={() => openModal('levels')}
                      >
                        <PixelIcon name="levels" size={14} color="#64B5F6" />
                        <span>Choose Level</span>
                      </div>
                      <div
                        className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-lg cursor-pointer hover:bg-white/20 transition-colors text-xs"
                        onClick={() => handleStartGame(Math.floor(Math.random() * puzzles.length))}
                      >
                        <PixelIcon name="dice" size={14} color="#FFC107" />
                        <span>Random</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="w-full flex justify-center text-sm mb-3">Next up...</p>
                    <p className="next-up woah">
                      {nextLevelNumber === 1 ? 'Tutorial' : `Level ${nextLevelNumber}`}
                    </p>
                    {nextLevelNumber >= 1 &&
                      nextLevelNumber <= puzzles.length &&
                      puzzles[nextLevelNumber - 1] && (
                        <PuzzlePreview
                          levelData={getPuzzleGrid(puzzles[nextLevelNumber - 1])}
                          layoutId="level-preview"
                          onClick={() => handleStartGame(nextLevelNumber - 1)}
                        />
                      )}
                  </>
                )}
              </div>

              {/* Story Mode buttons */}
              <div
                id="title-screen-buttons-container"
                className="title-screen-buttons-container w-full flex flex-col items-center"
                style={buttonContainerStyle}
              >
                <GameButton
                  onClick={() => (hasCompletedLevels ? handleContinueGame() : handleStartGame())}
                  variant={shopState.lives <= 0 ? 'danger' : 'primary'}
                  icon={
                    shopState.lives <= 0
                      ? 'heart'
                      : allLevelsCompleted()
                      ? 'star'
                      : hasCompletedLevels
                      ? 'continue'
                      : 'play'
                  }
                  text={
                    shopState.lives <= 0
                      ? 'No Lives - Visit Shop'
                      : allLevelsCompleted()
                      ? StorageService.getLastPlayedLevel() !== null
                        ? `Continue Level ${StorageService.getLastPlayedLevel() === 0 ? 'Tutorial' : StorageService.getLastPlayedLevel()! + 1}`
                        : 'Master Mode'
                      : hasCompletedLevels
                      ? 'Continue Game'
                      : 'Play Game'
                  }
                  shortcut="ENTER"
                  className="home-menu-btn"
                  layoutId="main-play-button"
                />

                <GameButton
                  onClick={() => openModal('levels')}
                  variant="secondary"
                  icon="levels"
                  text="Level Select"
                  className="home-menu-btn"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 6. High Scores (collapsible) */}
        <div style={{ width: '100%', maxWidth: LAYOUT.homeButtonMaxWidth, margin: '0 auto' }}>
          <motion.button
            type="button"
            onClick={() => setHighScoresExpanded(prev => !prev)}
            whileTap={{ scale: 0.97 }}
            style={{
              ...glassPillStyle(),
              width: '100%',
              justifyContent: 'center',
              gap: SPACING.sm,
            }}
          >
            <PixelIcon name="star" size={14} color={COLOR.starGold} />
            <span style={{ fontSize: FONT_SIZE.caption, fontFamily: FONT.body }}>
              High Scores
            </span>
            {totalScore > 0 && (
              <span style={{
                fontSize: FONT_SIZE.micro,
                fontFamily: FONT.body,
                color: COLOR.score,
                fontWeight: 'bold',
                marginLeft: SPACING.xs,
              }}>
                {totalScore.toLocaleString()}
              </span>
            )}
            <motion.span
              animate={{ rotate: highScoresExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', marginLeft: SPACING.xs }}
            >
              <PixelIcon name="chevronDown" size={10} color="rgba(255,255,255,0.5)" />
            </motion.span>
          </motion.button>

          <AnimatePresence>
            {highScoresExpanded && (
              <motion.div
                key="high-scores-detail"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ overflow: 'hidden', width: '100%', marginTop: SPACING.sm }}
              >
                <HighScores mode={activeMode} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* No Lives Modal */}
      <NoLivesModal
        isOpen={showNoLivesModal}
        onClose={() => setShowNoLivesModal(false)}
        onShop={() => {
          setShowNoLivesModal(false);
          openModal('shop');
        }}
      />

      {/* Daily Reward Modal */}
      <DailyRewardModal
        isOpen={showDailyRewardModal}
        onClose={() => {
          setShowDailyRewardModal(false);
          if (shopManager) {
            const status = shopManager.getDailyRewardStatus();
            setCanClaimDailyReward(status.canClaim);
            setDailyStreak(status.streak);
          }
        }}
      />

      {/* Shop Modal */}
      <ShopModal isOpen={isModalOpen('shop')} onClose={() => closeModal()} />

      {/* Level Select Modal */}
      <LevelSelectModal
        isOpen={isModalOpen('levels')}
        onClose={() => closeModal()}
        onLevelSelect={level => {
          closeModal();
          navigate(`/game?level=${level}`);
        }}
      />

      {/* Profile Modal */}
      <ProfileModal isOpen={isModalOpen('profile')} onClose={() => closeModal()} />

      {/* Settings Modal */}
      <SettingsMenuWrapper
        isOpen={isModalOpen('settings')}
        onClose={() => closeModal()}
        onRestart={() => {
          console.log('Cannot restart from home screen');
        }}
        onGoHome={() => {
          closeModal();
        }}
        onToggleFullscreen={() => {
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
          } else {
            document.exitFullscreen();
          }
        }}
      />

      {/* Character Customization Modal */}
      <CharacterCustomizationModal isOpen={isModalOpen('customize')} onClose={() => closeModal()} />
    </div>
  );
}

export default HomeScreen;
